import type { Object3D } from 'three'
import { BufferGeometry, FrontSide, Group, Line, LineBasicMaterial, Mesh, MeshBasicMaterial, Vector3 } from 'three'

import type { World } from '~/lib/three-d-resources/world'
import { rectangleRounded } from '@/lib/three-d-editor/utils'
import { getClosestVertex } from '@/lib/three-d-resources/geometry/utils'
import { DEFAULT_FONT_SIZE, RELATIVE_DEFAULT_LABEL_FLAGPOLE_DISTANCE } from '@/lib/constants/labels'

const BASE_GHOST_WIDTH = 10.25
const BASE_GHOST_HEIGHT = 2.25

export class LabelCreationVisualizer {
  private _ghost = new Group()
  private _ghostLabel: Mesh | null = null
  private _ghostCutoutMaterial = new MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5, side: FrontSide })
  private _flagpole: Line | null = null
  private _flagpoleIndex: number | undefined = undefined
  private _flagpoleMaterial = new LineBasicMaterial({ color: 0xffffff })
  private _flagpoleGeometry = new BufferGeometry()
  private _offset = new Vector3()

  constructor(private _world: World, private _orientToCamera: (ghost: Object3D) => void) {}

  initialize() {
    this._createGhost()
    this._world.scene.add(this._ghost)
    this._ghost.visible = false // hide by default until we hit a layer
  }

  private _createGhost() {
    if (this._ghostLabel) return

    const size = this._world.currentModel!.getTextScale() * DEFAULT_FONT_SIZE
    const geometry = rectangleRounded(BASE_GHOST_WIDTH * size, BASE_GHOST_HEIGHT * size, size / 4, 10)
    geometry.computeBoundingSphere()
    this._ghostLabel = new Mesh(geometry, this._ghostCutoutMaterial)
    this._ghostLabel.userData.isLabelCreationVisualizer = true

    this._flagpoleGeometry.setDrawRange(0, 2)
    this._flagpoleGeometry.setFromPoints([new Vector3(), new Vector3()])
    this._flagpole = new Line(this._flagpoleGeometry, this._flagpoleMaterial)
    this._flagpole.userData.isLabelCreationVisualizer = true

    this._ghost.add(this._ghostLabel)
    this._ghost.add(this._flagpole)
  }

  update(point: Vector3, normal: Vector3, cameraZoomDistance?: number | null) {
    if (!normal || !this._flagpole || !this._ghostLabel) {
      return
    }

    const size = this._world.currentModel!.getTextScale() * DEFAULT_FONT_SIZE
    // dividing cameraZoomDistance by size * 50 accommodates models of varying sizes
    // Flooring at 1 ensures text sizes don't go below the default font size of 16
    const cameraScaleFactor = cameraZoomDistance ? Math.max(cameraZoomDistance / (size * 50), 1) : 1
    this._offset = normal.clone().multiplyScalar(size * RELATIVE_DEFAULT_LABEL_FLAGPOLE_DISTANCE * cameraScaleFactor)
    const position = new Vector3().addVectors(point, this._offset)
    this._ghostLabel.position.copy(position)
    this._orientToCamera(this._ghostLabel)
    const newGeometry = rectangleRounded(BASE_GHOST_WIDTH * size, BASE_GHOST_HEIGHT * size, size / 4, 10)
    newGeometry.computeBoundingSphere()
    this._ghostLabel.geometry.dispose()
    this._ghostLabel.geometry = newGeometry
    this._ghostLabel.scale.setScalar(cameraScaleFactor)
    this._ghostLabel.geometry.computeBoundingSphere()

    const { pos, index } = getClosestVertex(this._ghostLabel, point)
    this._flagpoleIndex = index
    const positionAttribute = this._flagpole.geometry.getAttribute('position')
    positionAttribute.setXYZ(0, point.x, point.y, point.z)
    positionAttribute.setXYZ(1, pos.x, pos.y, pos.z)
    positionAttribute.needsUpdate = true
    this._flagpole.geometry.computeBoundingSphere()
  }

  // returns the current flagpole's label vertex attachment index
  getFlagpoleIndex(): number {
    if (this._flagpoleIndex === undefined) {
      throw new Error("Visualizer couldn't provide a flagpole index")
    }
    return this._flagpoleIndex
  }

  getLabelTextSize(): number {
    if (!this._ghostLabel) {
      throw new Error("Visualizer couldn't provide a label scale")
    }
    const textSize = this._ghostLabel.scale.clone().x * DEFAULT_FONT_SIZE
    return textSize
  }

  hide(): void {
    this._ghost.visible = false
  }

  show(): void {
    this._ghost.visible = true
  }

  dispose(): void {
    this._ghostCutoutMaterial.dispose()
    this._flagpoleGeometry.dispose()
    this._flagpoleMaterial.dispose()
    this._ghost.parent?.remove(this._ghost)
    this._ghost.clear()
  }
}
